import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const ENABLE_DEFAULT_PRODUCTS_SEED =
  process.env.REACT_APP_ENABLE_DEFAULT_PRODUCTS_SEED === 'true';

export type Product = {
  id: number;
  name: string;
  price: number;
  category: string;
  image: string;
  rating: number;
  reviews: number;
  colors: string[];
  sizes?: string[];
  isNew?: boolean;
  isFavorite: boolean;
  inStock: boolean;
  discount?: number;
  stock?: number;
  sizeStock?: Record<string, number>;
  description?: string;
  composition?: string;
  images?: string[];
};

interface ProductsSettings {
  lowStockThreshold: number;
}

export const defaultProducts: Product[] = [];

interface ProductsContextValue {
  products: Product[];
  addProduct: (
    product: Omit<Product, 'id'>,
    options?: { onCreated?: (created: Product) => void }
  ) => void;
  updateProduct: (id: number, updates: Partial<Omit<Product, 'id'>>) => void;
  deleteProduct: (id: number) => void;
  resetProducts: () => void;
  settings: ProductsSettings;
  setLowStockThreshold: (value: number) => void;
}

const ProductsContext = createContext<ProductsContextValue | undefined>(undefined);

const SETTINGS_KEY = 'products_settings';

const DEFAULT_SETTINGS: ProductsSettings = {
  lowStockThreshold: 5,
};

const loadInitialSettings = (): ProductsSettings => {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }

  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<ProductsSettings>;
      if (typeof parsed.lowStockThreshold === 'number') {
        return {
          lowStockThreshold: parsed.lowStockThreshold,
        };
      }
    }
  } catch (error) {
    console.error(
      'Erreur lors du chargement des paramètres produits depuis le localStorage',
      error
    );
  }

  return DEFAULT_SETTINGS;
};

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<ProductsSettings>(() => loadInitialSettings());

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setProducts(data as Product[]);
            return;
          }
        } else {
          console.error('Erreur lors du chargement des produits depuis le serveur');
        }
      } catch (error) {
        console.error('Erreur réseau lors du chargement des produits depuis le serveur', error);
      }

      if (!ENABLE_DEFAULT_PRODUCTS_SEED) {
        setProducts([]);
        return;
      }

      // Si aucun produit n'est présent en base (ou en cas d'erreur),
      // on tente d'initialiser le catalogue avec les produits par défaut côté serveur.
      try {
        for (const product of defaultProducts) {
          const { id: _ignoredId, ...withoutId } = product;
          await fetch(`${API_BASE_URL}/products`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(withoutId),
          });
        }

        const seededResponse = await fetch(`${API_BASE_URL}/products`);
        if (seededResponse.ok) {
          const seededData = await seededResponse.json();
          if (Array.isArray(seededData) && seededData.length > 0) {
            setProducts(seededData as Product[]);
            return;
          }
        }
      } catch (error) {
        console.error(
          "Erreur lors de l'initialisation des produits par défaut sur le serveur",
          error
        );
      }

      // Dernier recours : utiliser les produits par défaut uniquement en mémoire
      setProducts(defaultProducts);
    };

    void loadProducts();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error(
        'Erreur lors de la sauvegarde des paramètres produits dans le localStorage',
        error
      );
    }
  }, [settings]);

  const addProduct = (
    productWithoutId: Omit<Product, 'id'>,
    options?: { onCreated?: (created: Product) => void }
  ) => {
    const create = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(productWithoutId),
        });

        if (!response.ok) {
          console.error('Erreur lors de la création du produit côté serveur');
          return;
        }

        const created = (await response.json()) as Product;
        setProducts((prev) => [...prev, created]);
        if (options && typeof options.onCreated === 'function') {
          try {
            options.onCreated(created);
          } catch (error) {
            console.error('Erreur dans le callback onCreated de addProduct', error);
          }
        }
      } catch (error) {
        console.error('Erreur réseau lors de la création du produit', error);
      }
    };

    void create();
  };

  const updateProduct = (id: number, updates: Partial<Omit<Product, 'id'>>) => {
    const existing = products.find((p) => p.id === id);
    if (!existing) {
      return;
    }

    const payload: Product = {
      ...existing,
      ...updates,
    };

    const update = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          console.error('Erreur lors de la mise à jour du produit côté serveur');
          return;
        }

        const updated = (await response.json()) as Product;
        setProducts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      } catch (error) {
        console.error('Erreur réseau lors de la mise à jour du produit', error);
      }
    };

    void update();
  };

  const deleteProduct = (id: number) => {
    const remove = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          console.error('Erreur lors de la suppression du produit côté serveur');
          return;
        }

        setProducts((prev) => prev.filter((p) => p.id !== id));
      } catch (error) {
        console.error('Erreur réseau lors de la suppression du produit', error);
      }
    };

    void remove();
  };

  const resetProducts = () => {
    const reset = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          console.error('Erreur lors de la réinitialisation des produits côté serveur');
        }
      } catch (error) {
        console.error('Erreur réseau lors de la réinitialisation des produits', error);
      }

      if (!ENABLE_DEFAULT_PRODUCTS_SEED) {
        setProducts([]);
        return;
      }

      // Ensuite on réinsère les produits par défaut côté serveur
      try {
        for (const product of defaultProducts) {
          const { id: _ignoredId, ...withoutId } = product;
          await fetch(`${API_BASE_URL}/products`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(withoutId),
          });
        }

        const responseAfter = await fetch(`${API_BASE_URL}/products`);
        if (responseAfter.ok) {
          const data = await responseAfter.json();
          if (Array.isArray(data) && data.length > 0) {
            setProducts(data as Product[]);
            return;
          }
        }
      } catch (error) {
        console.error('Erreur lors de la réinitialisation des produits par défaut', error);
      }

      // Fallback : remettre simplement les produits par défaut en mémoire
      setProducts(defaultProducts);
    };

    void reset();
  };

  const setLowStockThreshold = (value: number) => {
    setSettings((prev) => ({ ...prev, lowStockThreshold: value }));
  };

  const value: ProductsContextValue = {
    products,
    addProduct,
    updateProduct,
    deleteProduct,
    resetProducts,
    settings,
    setLowStockThreshold,
  };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts doit être utilisé dans un ProductsProvider');
  }
  return context;
};
